import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Upload, FolderOpen, FolderPlus, FileText, FileBox, Trash2,
  Download, RotateCcw, ChevronRight, Home, Search, HardHat, Building2,
} from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatDate } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { BotonesReuniones, CalendarioReuniones, ActasReuniones } from "./archivos/ReunionesActas";   // Fix IR (2026-11-01)

// Auditoría 2026-08-27, CAMBIO 6 — Archivos de Empresa (CEN/ADM). Gestor
// documental de nivel empresa, distinto del gestor por obra (Obra > Archivos)
// y del de Ingeniería > Proyectos. Carpetas anidadas, papelera, búsqueda.

const opts = { withCredentials: true };
const SECCIONES = [
  { key: "CEN", label: "CEN", sub: "Estatuto, reglamento interno" },
  { key: "ADM", label: "ADM", sub: "IPS, oficina, impuestos" },
];

function FileIcon({ filename }) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ext === "dwg" || ext === "dxf") return <FileBox size={16} className="text-amber-500 flex-shrink-0" />;
  return <FileText size={16} className="text-gray-400 flex-shrink-0" />;
}

export default function ArchivosEmpresa() {
  const [tab, setTab] = useState("CEN");
  const [carpetas, setCarpetas] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [path, setPath] = useState([]); // [{id, nombre}]
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showPapelera, setShowPapelera] = useState(false);
  const [papelera, setPapelera] = useState([]);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const parentId = path.length ? path[path.length - 1].id : null;

  const load = useCallback(() => {
    if (tab === "Obras" || tab === "Calendario" || tab === "Actas") return;   // Fix IR: el calendario y las actas cargan lo suyo
    setLoading(true);
    const params = { seccion: tab };
    if (parentId) params.parent_id = parentId;
    axios.get(`${API}/archivos-empresa`, { ...opts, params })
      .then(({ data }) => { setCarpetas(data.carpetas || []); setArchivos(data.archivos || []); })
      .catch(() => toast.error("Error cargando archivos"))
      .finally(() => setLoading(false));
  }, [tab, parentId]);
  useEffect(load, [load]);

  const changeTab = (t) => { setTab(t); setPath([]); setShowPapelera(false); setSearchResults(null); };
  const entrarCarpeta = (c) => setPath([...path, { id: c.id, nombre: c.nombre }]);
  const irABreadcrumb = (idx) => setPath(idx < 0 ? [] : path.slice(0, idx + 1));

  const crearCarpeta = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/archivos-empresa/carpetas`, {
        seccion: tab, parent_id: parentId, nombre: newFolderName.trim(),
      }, opts);
      toast.success("Carpeta creada");
      setNewFolderName(""); setShowNewFolder(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear carpeta"); }
  };

  const eliminarCarpeta = async (id) => {
    try {
      await axios.delete(`${API}/archivos-empresa/carpetas/${id}`, opts);
      toast.success("Carpeta eliminada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const upload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("seccion", tab);
      if (parentId) fd.append("parent_id", parentId);
      Array.from(files).forEach((f) => fd.append("files", f));
      await axios.post(`${API}/archivos-empresa/upload`, fd, opts);
      toast.success("Archivo(s) subido(s)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir (máx. 12 MB por archivo)"); }
    finally { setUploading(false); }
  };

  const download = (id) => window.open(`${API}/archivos-empresa/${id}/download`, "_blank");

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API}/archivos-empresa/${id}`, opts);
      toast.success("Enviado a la papelera");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const abrirPapelera = async () => {
    setShowPapelera(true);
    try {
      const { data } = await axios.get(`${API}/archivos-empresa/papelera`, { ...opts, params: { seccion: tab } });
      setPapelera(data || []);
    } catch { toast.error("Error cargando papelera"); }
  };

  const restaurar = async (id) => {
    try {
      await axios.post(`${API}/archivos-empresa/${id}/restore`, {}, opts);
      toast.success("Archivo restaurado");
      abrirPapelera(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const buscar = async (e) => {
    e.preventDefault();
    if (!query.trim()) { setSearchResults(null); return; }
    try {
      const { data } = await axios.get(`${API}/archivos-empresa/search`, { ...opts, params: { q: query.trim(), seccion: tab } });
      setSearchResults(data || []);
    } catch { toast.error("Error al buscar"); }
  };

  return (
    <div className="space-y-4" data-testid="archivos-empresa-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Archivos de Empresa</h1>
        <p className="text-sm text-gray-500 mt-0.5">Documentos de la empresa (CEN, ADM), acceso directo a Obras, calendario de reuniones y actas</p>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {SECCIONES.map((s) => (
          <button key={s.key} onClick={() => changeTab(s.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${tab === s.key ? "bg-[#0EA5E9] text-white border-[#0EA5E9]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
            data-testid={`archivos-empresa-tab-${s.key}`}>
            {s.label} <span className="opacity-70 text-xs">· {s.sub}</span>
          </button>
        ))}
        <button onClick={() => changeTab("Obras")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all flex items-center gap-1.5 ${tab === "Obras" ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
          data-testid="archivos-empresa-tab-Obras">
          <HardHat size={14} /> Obras
        </button>
        {/* Fix IR (2026-11-01): Calendario de reuniones y Actas. */}
        <BotonesReuniones tab={tab} onChange={changeTab} />
      </div>

      {tab === "Calendario" ? <CalendarioReuniones /> : tab === "Actas" ? <ActasReuniones /> : tab === "Obras" ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-10 text-center shadow-sm" data-testid="archivos-empresa-obras-link">
          <Building2 size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-700 font-medium mb-1">Los archivos de cada obra se gestionan en su propio perfil</p>
          <p className="text-sm text-gray-400 mb-4">Ingresá a Obra, elegí la obra y abrí la pestaña "Archivos"</p>
          <Link to="/obra" className="inline-flex items-center gap-2 px-4 py-2 bg-[#EA580C] text-white text-sm font-semibold rounded-xl hover:bg-[#c2490a]" data-testid="archivos-empresa-ir-a-obras">
            Ir a Obras <ChevronRight size={14} />
          </Link>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap" data-testid="archivos-empresa-breadcrumb">
              <button onClick={() => irABreadcrumb(-1)} className="flex items-center gap-1 hover:text-[#0EA5E9] font-medium"><Home size={12} />{tab}</button>
              {path.map((p, idx) => (
                <span key={p.id} className="flex items-center gap-1">
                  <ChevronRight size={12} />
                  <button onClick={() => irABreadcrumb(idx)} className={`hover:text-[#0EA5E9] ${idx === path.length - 1 ? "font-bold text-gray-800" : ""}`}>{p.nombre}</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <form onSubmit={buscar} className="flex items-center gap-1">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre..."
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-40" data-testid="archivos-empresa-search-input" />
                <button type="submit" className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-[#0EA5E9]" data-testid="archivos-empresa-search-btn"><Search size={13} /></button>
              </form>
              <button onClick={abrirPapelera} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="archivos-empresa-papelera-btn">
                <RotateCcw size={12} /> Papelera
              </button>
            </div>
          </div>

          {searchResults ? (
            <div className="bg-white rounded-2xl border border-gray-200/60 divide-y divide-gray-50" data-testid="archivos-empresa-search-results">
              <div className="px-4 py-2 flex items-center justify-between">
                <p className="text-xs font-bold text-gray-500 uppercase">Resultados: {searchResults.length}</p>
                <button onClick={() => { setSearchResults(null); setQuery(""); }} className="text-xs text-gray-400 hover:text-gray-600">Cerrar búsqueda</button>
              </div>
              {searchResults.length === 0 ? <p className="px-4 py-3 text-sm text-gray-400">Sin resultados</p> : searchResults.map((a) => (
                <div key={a._id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 min-w-0"><FileIcon filename={a.filename} /><span className="text-sm text-gray-800 truncate">{a.filename}</span></div>
                  <button onClick={() => download(a._id)} className="text-gray-400 hover:text-[#0EA5E9]"><Download size={15} /></button>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex-1 flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-2xl px-4 py-4 text-sm text-gray-500 cursor-pointer justify-center hover:border-[#0EA5E9]">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Subir archivo aquí (máx. 12 MB, incluye PDF/imagen/DWG/DXF/Office)
                  <input type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="archivos-empresa-upload-input" />
                </label>
                <button onClick={() => setShowNewFolder(true)} className="flex items-center gap-1.5 px-3 py-4 rounded-2xl border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#0EA5E9] hover:text-[#0EA5E9]" data-testid="archivos-empresa-nueva-carpeta-btn">
                  <FolderPlus size={16} /> Nueva carpeta
                </button>
              </div>

              {showNewFolder && (
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-2" data-testid="archivos-empresa-nueva-carpeta-form">
                  <input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Nombre de la carpeta"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm" data-testid="archivos-empresa-nueva-carpeta-input"
                    onKeyDown={(e) => e.key === "Enter" && crearCarpeta()} />
                  <button onClick={crearCarpeta} className="px-3 py-1.5 rounded-lg bg-[#0EA5E9] text-white text-xs font-semibold" data-testid="archivos-empresa-nueva-carpeta-confirmar">Crear</button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} className="px-3 py-1.5 rounded-lg text-xs text-gray-500">Cancelar</button>
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
              ) : (
                <>
                  {carpetas.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {carpetas.map((f) => (
                        <div key={f.id} className="group relative bg-white border border-gray-200/60 rounded-xl p-3 flex items-center gap-2 cursor-pointer hover:border-[#0EA5E9]" onClick={() => entrarCarpeta(f)} data-testid={`archivos-empresa-carpeta-${f.id}`}>
                          <FolderOpen size={18} className="text-[#0EA5E9] flex-shrink-0" />
                          <span className="text-xs font-medium text-gray-700 truncate flex-1">{f.nombre}</span>
                          <button onClick={(e) => { e.stopPropagation(); eliminarCarpeta(f.id); }} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500" data-testid={`archivos-empresa-carpeta-del-${f.id}`}><Trash2 size={13} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  {archivos.length === 0 && carpetas.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm" data-testid="archivos-empresa-empty">
                      <FolderOpen size={36} className="mx-auto text-gray-200 mb-3" />
                      <p className="text-gray-600 font-medium">Carpeta vacía</p>
                    </div>
                  ) : archivos.length > 0 && (
                    <div className="bg-white rounded-2xl border border-gray-200/60 divide-y divide-gray-50">
                      {archivos.map((a) => (
                        <div key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`archivos-empresa-row-${a.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileIcon filename={a.filename} />
                            <div className="min-w-0">
                              <p className="text-sm text-gray-800 truncate">{a.filename}</p>
                              <p className="text-xs text-gray-400">{formatDate(a.uploaded_at)} · {(a.size / 1024).toFixed(0)} KB · {a.uploaded_by_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <button onClick={() => download(a.id)} className="text-gray-400 hover:text-[#0EA5E9]" data-testid={`archivos-empresa-download-${a.id}`}><Download size={15} /></button>
                            <button onClick={() => eliminar(a.id)} className="text-gray-300 hover:text-red-500" data-testid={`archivos-empresa-del-${a.id}`}><Trash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {showPapelera && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="archivos-empresa-papelera-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-amber-700 uppercase">Papelera — {tab}</p>
                <button onClick={() => setShowPapelera(false)} className="text-xs text-gray-400 hover:text-gray-600">Cerrar</button>
              </div>
              {papelera.length === 0 ? <p className="text-xs text-gray-400">Vacía</p> : papelera.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-xs py-1">
                  <span>{a.filename}</span>
                  <button onClick={() => restaurar(a.id)} className="text-emerald-600 font-semibold hover:underline" data-testid={`archivos-empresa-restore-${a.id}`}>Restaurar</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
